import {create} from 'zustand';
import {API_BASE_URL} from '@/api/client';

interface ConnectivityState {
  /** Raw navigator.onLine value */
  browserOnline: boolean;
  /** Last heartbeat result — is the server reachable? */
  serverReachable: boolean;
  /** Combined verdict: browserOnline && serverReachable */
  isOnline: boolean;
  /** Timestamp of last heartbeat check */
  lastCheckedAt: Date | null;

  setBrowserOnline: (online: boolean) => void;
  setServerReachable: (reachable: boolean) => void;
}

const initialState = {
  browserOnline: true,
  serverReachable: true,
  isOnline: true,
  lastCheckedAt: null as Date | null,
};

export const useConnectivityStore = create<ConnectivityState>((set) => ({
  ...initialState,

  setBrowserOnline: (online) =>
    set((state) => ({
      browserOnline: online,
      isOnline: online && state.serverReachable,
    })),

  setServerReachable: (reachable) =>
    set((state) => ({
      serverReachable: reachable,
      isOnline: state.browserOnline && reachable,
      lastCheckedAt: new Date(),
    })),
}));

export function resetConnectivityStore() {
  useConnectivityStore.setState(initialState);
}

/** Heartbeat interval when online (30 seconds) */
const HEARTBEAT_ONLINE_MS = 30_000;

/** Heartbeat interval when server is unreachable (5 seconds — faster retry) */
const HEARTBEAT_OFFLINE_MS = 5_000;

/** Health endpoint for heartbeat pings */
const HEALTH_URL = `${API_BASE_URL}/health`;

let activeCleanup: (() => void) | null = null;

/**
 * Initialize connectivity listeners:
 * - Reads initial navigator.onLine
 * - Subscribes to browser online/offline events
 * - Starts a heartbeat ping to the health endpoint
 *
 * Returns a cleanup function that removes all listeners and stops the heartbeat.
 */
export function initConnectivityListeners(): () => void {
  // Tear down previous instance if re-initialized (React strict mode safety)
  if (activeCleanup) activeCleanup();

  // Read initial browser state
  useConnectivityStore.getState().setBrowserOnline(navigator.onLine);

  // Browser event handlers
  function handleOnline() {
    useConnectivityStore.getState().setBrowserOnline(true);
  }

  function handleOffline() {
    useConnectivityStore.getState().setBrowserOnline(false);
  }

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Heartbeat
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  async function heartbeat() {
    if (stopped) return;

    try {
      await fetch(HEALTH_URL, {
        method: 'GET',
        cache: 'no-store',
        // Short timeout for heartbeat — don't block on slow connections
        signal: AbortSignal.timeout(5_000),
      });
      useConnectivityStore.getState().setServerReachable(true);
    } catch {
      useConnectivityStore.getState().setServerReachable(false);
    }

    if (stopped) return;

    // Schedule next heartbeat — shorter interval when server is unreachable
    const interval = useConnectivityStore.getState().serverReachable
      ? HEARTBEAT_ONLINE_MS
      : HEARTBEAT_OFFLINE_MS;
    heartbeatTimer = setTimeout(() => void heartbeat(), interval);
  }

  // Fire initial heartbeat asynchronously
  void heartbeat();

  // Cleanup function
  const cleanup = () => {
    stopped = true;
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    if (heartbeatTimer !== null) {
      clearTimeout(heartbeatTimer);
      heartbeatTimer = null;
    }
    activeCleanup = null;
  };

  activeCleanup = cleanup;
  return cleanup;
}
